import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minus,
  Plus,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ===========================================================================
// El PDF del banco, al lado de sus líneas.
//
// Contabilidad lo pidió así: «ver la tabla en el lado izquierdo y el PDF en el
// lado derecho, para poder verlo todo desde una pantalla». Y es lo que pide el
// trabajo: corregir una línea es compararla contra el papel, y hacerlo con dos
// pestañas obliga a memorizar una cifra de siete dígitos para ir a escribirla.
//
// LAS PÁGINAS SE PIDEN DIBUJADAS, una por una, a `/pagina/:archivo/:n`. No se
// sirve el PDF original a un `<iframe>`, que sería lo cómodo: ese endpoint
// dibuja tachando los RUC del grupo, y servir el archivo tal cual salta la
// tachadura. Cuesta una petición por hoja y se paga.
//
// El zoom es `transform: scale` y no `width`, por lo mismo que en el visor de
// vouchers: con `width` el navegador reescala la imagen ya decodificada y el
// texto se empasta justo cuando se amplía para leerlo.
// ===========================================================================

const ZOOM_MIN = 0.6;
const ZOOM_MAX = 4;
const PASO = 0.25;

export function PapelDelExtracto({
  archivoId,
  paginas,
  alAgrandar,
  /**
   * Cómo se llama el papel. Por defecto el del extracto, que es de donde
   * salió este panel — pero también sirve al lado de un expediente de cliente,
   * y ahí «el PDF del banco» es sencillamente falso.
   */
  titulo = "El PDF del banco",
}: {
  archivoId: string;
  /** Cuántas hojas tiene. `null` si no se sabe: entonces no se pasa página. */
  paginas: number | null;
  /** Abrir el visor a pantalla completa. */
  alAgrandar: (pagina: number) => void;
  titulo?: string;
}) {
  const [pagina, setPagina] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [fallo, setFallo] = useState(false);
  const total = paginas ?? 1;

  const ir = (n: number) => {
    const siguiente = Math.min(Math.max(n, 1), total);
    if (siguiente === pagina) return;
    setPagina(siguiente);
    setFallo(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border bg-muted/30">
      <div className="flex flex-wrap items-center gap-1.5 border-b px-2.5 py-2">
        <span className="font-semibold text-[10.5px] text-muted-foreground uppercase tracking-wider">
          {titulo}
        </span>

        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            disabled={zoom <= ZOOM_MIN}
            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - PASO))}
            aria-label="Alejar"
          >
            <Minus className="size-3.5" />
          </Button>
          <span className="w-[42px] text-center text-[11px] text-muted-foreground tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            disabled={zoom >= ZOOM_MAX}
            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + PASO))}
            aria-label="Acercar"
          >
            <Plus className="size-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            onClick={() => alAgrandar(pagina)}
            aria-label="Ver a pantalla completa"
          >
            <Maximize2 className="size-3.5" />
          </Button>
        </div>
      </div>

      {/* La hoja. `overflow-auto` en su propio contenedor: al ampliar, la
          imagen crece y hay que poder recorrerla sin mover la página entera. */}
      <div className="min-h-0 flex-1 overflow-auto bg-neutral-100 p-2 dark:bg-neutral-900">
        {fallo ? (
          <p className="px-3 py-8 text-center text-[12px] text-muted-foreground">
            No se pudo dibujar esta hoja. Puede que el PDF venga dañado, o que
            el lector no esté levantado.
          </p>
        ) : (
          <img
            // La `key` fuerza a pedir la hoja de nuevo al pasar de página: sin
            // ella el navegador reusa la imagen anterior mientras carga y se ve
            // la hoja vieja bajo el número nuevo.
            key={`${archivoId}-${pagina}`}
            src={`/pagina/${archivoId}/${pagina}`}
            alt={`Hoja ${pagina} del extracto`}
            onError={() => setFallo(true)}
            style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
            className="w-full bg-white shadow-sm"
          />
        )}
      </div>

      {total > 1 ? (
        <div className="flex items-center justify-between border-t px-2.5 py-1.5">
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            disabled={pagina <= 1}
            onClick={() => ir(pagina - 1)}
            aria-label="Hoja anterior"
          >
            <ChevronLeft className="size-3.5" />
          </Button>
          <span className="text-[11.5px] text-muted-foreground tabular-nums">
            Hoja {pagina} de {total}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            disabled={pagina >= total}
            onClick={() => ir(pagina + 1)}
            aria-label="Hoja siguiente"
          >
            <ChevronRight className="size-3.5" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** El botón que abre y cierra el panel del papel. */
export function BotonDelPapel({
  abierto,
  alPulsar,
}: {
  abierto: boolean;
  alPulsar: () => void;
}) {
  return (
    <Button
      size="sm"
      variant={abierto ? "default" : "outline"}
      onClick={alPulsar}
      className={cn(abierto && "shadow-sm")}
    >
      {abierto ? "Ocultar el PDF" : "Ver el PDF al lado"}
    </Button>
  );
}
